import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './styles/cell-classes.css';
import * as Sentry from '@sentry/react';
import { initSentry, getDefaultSentryConfig } from '@raporgor/utils';
import { ErrorBoundary, DefaultErrorFallback } from '@raporgor/shared-ui';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import App from './App';

dayjs.locale('tr');

// Sentry — demo panel ayrı proje (raporgor-demo); release vite-plugin'den gelir.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  const sentryConfig = getDefaultSentryConfig(sentryDsn, {
    deployable: 'demo',
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    isDevelopment: import.meta.env.DEV,
  });
  initSentry(Sentry, sentryConfig);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
  console.error('[App Error]', error);
  if (sentryDsn) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary onError={handleError} fallback={DefaultErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
